import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Tooltip,
  Typography,
} from '@mui/material';
import { groupBy, map, sortBy, startCase, toLower } from 'lodash-es';
import { useContext } from './context';
import { getPermissionsPreview } from '../../../lib/api';

const GROUP_CONFIG = {
  Admin: { color: 'error' },
  Manage: { color: 'warning' },
  View: { color: 'success' },
  Download: { color: 'secondary' },
  Run: { color: 'info' },
};

function getGroup(name) {
  const prefix = startCase(toLower(name.split('_')[0]));
  return GROUP_CONFIG[prefix] ? prefix : 'Other';
}

function groupPermissions(permissions) {
  const grouped = groupBy(permissions, (p) => getGroup(p.name));
  const keys = Object.keys(GROUP_CONFIG);

  return sortBy(
    map(grouped, (items, label) => ({
      label,
      color: GROUP_CONFIG[label]?.color || 'default',
      items: items.map((p) => {
        const prefix = `${p.name.split('_')[0]}_`;
        const isKnownGroup = GROUP_CONFIG[label];
        return {
          ...p,
          shortLabel: isKnownGroup
            ? startCase(toLower(p.name.slice(prefix.length)))
            : startCase(toLower(p.name)),
        };
      }),
    })),
    (group) => {
      const index = keys.indexOf(group.label);
      return index === -1 ? keys.length : index;
    },
  );
}

export function PermissionsPreview(props) {
  const { user } = useContext();
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(false);

  const queryParams = useMemo(() => {
    const username = user.username;
    const roles = user.roles?.map((r) => r.id) || [];
    const organizations = user.organizations?.map((o) => o.id) || [];

    if (!username || roles.length === 0 || organizations.length === 0) {
      return null;
    }

    return { username, roles, organizations };
  }, [user.roles, user.organizations, user.username]);

  useEffect(() => {
    if (!queryParams) {
      setPermissions([]);
      return;
    }

    const abortController = new AbortController();

    setLoading(true);
    getPermissionsPreview(queryParams, { signal: abortController.signal })
      .then((result) => setPermissions(result || []))
      .catch(() => {
        if (!abortController.signal.aborted) setPermissions([]);
      })
      .finally(() => {
        if (!abortController.signal.aborted) setLoading(false);
      });

    return () => abortController.abort();
  }, [queryParams]);

  const groups = useMemo(() => groupPermissions(permissions), [permissions]);

  if (!queryParams) {
    return null;
  }

  return (
    <Box {...props}>
      <Typography variant="h6">Permissions</Typography>

      {loading ? (
        <CircularProgress size={24} sx={{ mt: 2 }} />
      ) : (
        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {groups.map((group) => (
            <Box key={group.label}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 'bold' }}
              >
                {group.label}
              </Typography>
              <Box
                sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}
              >
                {group.items.map((permission) => (
                  <Tooltip
                    key={permission.name}
                    title={permission.description}
                    arrow
                  >
                    <Chip
                      label={permission.shortLabel}
                      size="small"
                      color={group.color}
                      variant="outlined"
                    />
                  </Tooltip>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
